import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  LoadingState,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2Card,
  V2FormField,
  V2FormGrid,
} from '@sakhtar/design-system/react';
import { ApiError, apiPut } from '../../lib/apiClient.js';
import { faDigits } from '../../lib/faDigits.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { useCapabilities } from '../../lib/capabilities.js';
import type {
  CommissionAccountOption,
  PostingSettingsResponse,
} from '../../lib/types/commission.js';
import { useApiGet } from '../../lib/useApiGet.js';

/**
 * «حساب‌های فروش و پورسانت» — where finance decides what a sales invoice and a
 * commission accrual post to. These choices used to sit on the salesperson's
 * screens (the invoice dialog and «پورسانت فروشندگان»); a seller does not
 * choose ledger accounts, so they live here and the sales screens only show
 * the result.
 *
 * UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2.dc.html — frame `/t/kimia/finance`
 *   (V2Card sections inside the finance module). No dedicated frame exists for
 *   this settings page; it follows the module's card/form language.
 * status: KIT_CONFORMANT, V2-only. Permission: `sales.posting-account.read`
 *   to view, `.manage` to save (server-enforced).
 */
export function SalesAccountsPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const { hasCapability } = useCapabilities();
  const [refreshKey, setRefreshKey] = useState(0);
  const response = useApiGet<PostingSettingsResponse>(
    slug ? `${base}/api/v1/sales/commission/posting-settings` : null,
    [slug, refreshKey],
  );
  if (!slug) return null;
  if (response.status === 'loading') return <LoadingState />;
  if (response.status === 'error') {
    return (
      <StatePanel
        variant={response.error.status === 403 ? 'permission_denied' : 'network_error'}
        title={
          response.error.status === 403
            ? 'تنظیم حساب‌های فروش فقط برای تیم مالی است'
            : 'تنظیمات قابل نمایش نیست'
        }
      />
    );
  }
  const canManage = hasCapability('sales.posting-account.manage');
  const refresh = () => setRefreshKey((k) => k + 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {!canManage ? (
        <V2Alert tone="info">
          شما فقط می‌توانید این تنظیمات را ببینید؛ تغییر آن با مجوز مالی است.
        </V2Alert>
      ) : null}
      <InvoiceAccountsCard
        base={base}
        data={response.data}
        canManage={canManage}
        onSaved={refresh}
      />
      <CommissionAccountsCard
        base={base}
        data={response.data}
        canManage={canManage}
        onSaved={refresh}
      />
    </div>
  );
}

function errorText(error: unknown): string {
  if (error instanceof ApiError) {
    return error.envelope.error.details?.[0]?.message ?? error.message;
  }
  return 'ذخیره انجام نشد؛ دوباره تلاش کنید.';
}

function AccountSelect({
  label,
  value,
  onChange,
  options,
  disabled,
  optional = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly CommissionAccountOption[];
  readonly disabled: boolean;
  readonly optional?: boolean;
}) {
  return (
    <V2FormField label={label} required={!optional} width="wide">
      <select
        className="sk-select"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{optional ? '— بدون (فقط اگر مالیات دارید) —' : '— انتخاب حساب —'}</option>
        {options.map((account) => (
          <option key={account.accountId} value={account.accountId}>
            {faDigits(account.code)} — {account.name}
          </option>
        ))}
      </select>
    </V2FormField>
  );
}

function CardShell({
  title,
  configured,
  description,
  children,
}: {
  readonly title: string;
  readonly configured: boolean;
  readonly description: string;
  readonly children: React.ReactNode;
}) {
  return (
    <V2Card>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>{title}</span>
          {configured ? (
            <V2Badge tone="success">تنظیم شده</V2Badge>
          ) : (
            <V2Badge tone="warn">تنظیم نشده</V2Badge>
          )}
        </div>
        <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)', lineHeight: 1.9 }}>
          {description}
        </span>
        {children}
      </div>
    </V2Card>
  );
}

function useSave(onSaved: () => void) {
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);
  async function run(action: () => Promise<unknown>, success: string) {
    setSaving(true);
    setMessage(null);
    try {
      await action();
      setMessage({ tone: 'success', text: success });
      onSaved();
    } catch (error) {
      setMessage({ tone: 'danger', text: errorText(error) });
    } finally {
      setSaving(false);
    }
  }
  return { saving, message, run };
}

function SaveRow({
  disabled,
  saving,
  message,
  onSave,
  label,
}: {
  readonly disabled: boolean;
  readonly saving: boolean;
  readonly message: { tone: 'success' | 'danger'; text: string } | null;
  readonly onSave: () => void;
  readonly label: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <V2Button variant="primary" disabled={disabled || saving} onClick={onSave}>
        {saving ? 'در حال ذخیره…' : label}
      </V2Button>
      {message ? (
        <span
          role={message.tone === 'danger' ? 'alert' : 'status'}
          style={{
            fontSize: 12,
            color: message.tone === 'danger' ? 'var(--sk-danger)' : 'var(--sk-success)',
          }}
        >
          {message.text}
        </span>
      ) : null}
    </div>
  );
}

function InvoiceAccountsCard({
  base,
  data,
  canManage,
  onSaved,
}: {
  readonly base: string;
  readonly data: PostingSettingsResponse;
  readonly canManage: boolean;
  readonly onSaved: () => void;
}) {
  const [receivable, setReceivable] = useState(data.sales.receivableAccountId ?? '');
  const [revenue, setRevenue] = useState(data.sales.revenueAccountId ?? '');
  const [vat, setVat] = useState(data.sales.vatAccountId ?? '');
  const { saving, message, run } = useSave(onSaved);
  useEffect(() => {
    setReceivable(data.sales.receivableAccountId ?? '');
    setRevenue(data.sales.revenueAccountId ?? '');
    setVat(data.sales.vatAccountId ?? '');
  }, [data.sales.receivableAccountId, data.sales.revenueAccountId, data.sales.vatAccountId]);
  return (
    <CardShell
      title="مقصد فاکتور فروش"
      configured={data.sales.configured}
      description="هر فاکتور فروش با این حساب‌ها سند درآمد پیش‌نویس می‌سازد: بدهکار حساب دریافتنی، بستانکار درآمد فروش و (در صورت مالیات) بستانکار مالیات بر ارزش افزوده. فروشنده هنگام صدور فاکتور حساب انتخاب نمی‌کند و فقط مقصد را می‌بیند."
    >
      <V2FormGrid>
        <AccountSelect
          label="حساب دریافتنی (بدهکار)"
          value={receivable}
          onChange={setReceivable}
          options={data.accounts}
          disabled={!canManage}
        />
        <AccountSelect
          label="حساب درآمد فروش (بستانکار)"
          value={revenue}
          onChange={setRevenue}
          options={data.accounts}
          disabled={!canManage}
        />
        <AccountSelect
          label="حساب مالیات بر ارزش افزوده (بستانکار)"
          value={vat}
          onChange={setVat}
          options={data.accounts}
          disabled={!canManage}
          optional
        />
      </V2FormGrid>
      {canManage ? (
        <SaveRow
          label="ذخیرهٔ مقصد فاکتور"
          disabled={!receivable || !revenue}
          saving={saving}
          message={message}
          onSave={() =>
            void run(
              () =>
                apiPut(`${base}/api/v1/sales/commission/posting-settings`, {
                  receivableAccountId: receivable,
                  revenueAccountId: revenue,
                  vatAccountId: vat || undefined,
                }),
              'مقصد فاکتور فروش ذخیره شد.',
            )
          }
        />
      ) : null}
    </CardShell>
  );
}

function CommissionAccountsCard({
  base,
  data,
  canManage,
  onSaved,
}: {
  readonly base: string;
  readonly data: PostingSettingsResponse;
  readonly canManage: boolean;
  readonly onSaved: () => void;
}) {
  const [expense, setExpense] = useState(data.commission.expenseAccountId ?? '');
  const [payable, setPayable] = useState(data.commission.payableAccountId ?? '');
  const { saving, message, run } = useSave(onSaved);
  useEffect(() => {
    setExpense(data.commission.expenseAccountId ?? '');
    setPayable(data.commission.payableAccountId ?? '');
  }, [data.commission.expenseAccountId, data.commission.payableAccountId]);
  return (
    <CardShell
      title="حساب‌های سند پورسانت"
      configured={data.commission.configured}
      description="با قطعی شدن هر فاکتور، سند پیش‌نویس «بدهکار هزینه پورسانت / بستانکار پورسانت پرداختنی» ساخته می‌شود و مثل بقیه اسناد در حسابداری تأیید و ثبت می‌شود. تا این دو حساب تنظیم نشده، هیچ طرح پورسانتی فعال نمی‌شود."
    >
      <V2FormGrid>
        <AccountSelect
          label="حساب هزینه پورسانت"
          value={expense}
          onChange={setExpense}
          options={data.accounts}
          disabled={!canManage}
        />
        <AccountSelect
          label="حساب پورسانت پرداختنی"
          value={payable}
          onChange={setPayable}
          options={data.accounts}
          disabled={!canManage}
        />
      </V2FormGrid>
      {canManage ? (
        <SaveRow
          label="ذخیرهٔ حساب‌های پورسانت"
          disabled={!expense || !payable}
          saving={saving}
          message={message}
          onSave={() =>
            void run(
              () =>
                apiPut(`${base}/api/v1/sales/commission/settings`, {
                  expenseAccountId: expense,
                  payableAccountId: payable,
                }),
              'حساب‌های پورسانت ذخیره شد.',
            )
          }
        />
      ) : null}
    </CardShell>
  );
}
